import React, { useEffect, useState } from "react";
import { getCookie, logAction } from "./log.js";
import folderIcon from '../assets/folder.png';
import zoomIn from '../assets/zoom.png';
import zoomOut from '../assets/dezoom.png';
import loadingGif from '../assets/loading.gif';
import '../css/PopUpMoveFile.css';
import { Folder } from 'iconoir-react';

/**
 * Popup permettant de déplacer un fichier ou un dossier vers une destination choisie dans l'arborescence.
 *
 * @param {Object} props
 * @param {Object} props.object - Objet représentant le fichier ou dossier à déplacer (avec id, name, path, type).
 * @param {number|string} props.dossier_id - ID du dossier courant (pour recharger l'arborescence).
 * @param {string} props.rootFolder - Chemin racine utilisé pour récupérer l'arborescence.
 * @param {function} props.onClose - Fonction appelée pour fermer la popup.
 * @param {function} [props.onConfirm] - Fonction optionnelle appelée après confirmation du déplacement.
 */
const PopUpMoveObject = ({ object, dossier_id, rootFolder, onClose, onConfirm }) => {
    const [treeData, setTreeData] = useState(null);  // Données de l'arborescence adaptée pour affichage
    const [zoom, setZoom] = useState(1);             // Niveau de zoom de la vue arborescente
    const [openFolders, setOpenFolders] = useState({});  // Etat des dossiers ouverts (path => bool)
    const [selectedPath, setSelectedPath] = useState(null); // Chemin du dossier sélectionné comme destination
    const API_URL = process.env.REACT_APP_API_URL;

    // Transforme les données backend en format adapté pour le rendu récursif
    const adaptNode = (node) => ({
        name: node.nom,
        type: node.type,
        children: node.children ? node.children.map(adaptNode) : []
    });

    // Charge l'arborescence depuis l'API au montage et à chaque changement de dossier ou objet
    useEffect(() => {
        const fetchTree = async () => {
            try {
                const res = await fetch(`${API_URL}/api/getArborescence.php`, {
                    method: "POST",
                    credentials: 'include',
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ dossier_id: object?.id, repertoire: rootFolder }),
                });
                const data = await res.json();
                const tree = data.tree || data;

                if (tree && Array.isArray(tree)) {
                    const adaptedTree = tree.map(adaptNode);
                    setTreeData(adaptedTree);
                } else {
                    setTreeData(null);
                }
            } catch (err) {
                console.error("Erreur lors du chargement de l'arborescence :", err);
            }
        };

        fetchTree();
    }, [dossier_id, object]);

    // Ouvre/ferme un dossier dans l'arborescence en modifiant son état
    const toggleFolder = (path) => {
        setOpenFolders(prev => ({
            ...prev,
            [path]: !prev[path]
        }));
    };

    /**
     * Fonction récursive pour afficher l'arborescence.
     * Chaque dossier peut être ouvert ou fermé.
     * La sélection d'un dossier modifie le path sélectionné.
     */
    const renderNode = (node, path = "") => {
        const safeName = node.name.replace(/\\/g, "/");
        const currentNodePath = path ? `${path}/${safeName}` : safeName;
        const isOpen = openFolders[currentNodePath];

        return (
            <div key={currentNodePath} className={`folder-block${isOpen ? " open" : ""}`}>
                <div
                    className={`folder-name${node.type !== "D" ? " file" : ""} ${selectedPath === currentNodePath ? "selected" : ""}`}
                    onClick={() => {
                        if (node.type === "D") {
                            toggleFolder(currentNodePath);
                        }
                        setSelectedPath(currentNodePath);
                    }}
                >
                    {/* <img src={folderIcon} width="18" height="18" alt="folder" /> */}
                    <Folder width="18" height="18" />
                    <span>{node.name}</span>
                </div>
                {isOpen && node.children?.length > 0 && (
                    <div className="folder-content">
                        {node.children.map(child => renderNode(child, currentNodePath))}
                    </div>
                )}
            </div>
        );
    };

    /**
     * Confirme le déplacement en appelant l'API.
     * Enregistre l'action dans les logs.
     * Ferme la popup et déclenche la callback onConfirm si elle est fournie.
     */
    const handleConfirm = async () => {
        if (!selectedPath) {
            alert("Veuillez sélectionner un dossier de destination.");
            return;
        }

        const newPath = `${selectedPath}\\${object.name}`;

        try {
            const res = await fetch(`${API_URL}/api/modifyObject.php`, {
                method: "PUT",
                credentials: 'include',
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    dossier: object.id,
                    old: object.path,
                    new: newPath,
                    action: "move",
                    type: object.type
                })
            });

            const data = await res.json();
            if (res.ok) {
                logAction({
                    action: "moveFile",
                    dossierId: object.id,
                    utilisateurId: getCookie("userId"),
                    fichier: object.name,
                    path: newPath,
                });
                onClose();
                if (onConfirm) onConfirm();
            } else {
                console.error("Erreur API :", data);
                alert("Erreur lors du déplacement du fichier/dossier.");
            }
        } catch (err) {
            console.error("Erreur réseau :", err);
            alert("Erreur réseau lors du déplacement.");
        }
    };

    return (
        <div className="popupMove-overlay">
            <div className="popupMove-content">
                <h3>Déplacer le fichier / dossier</h3>

                {/* <div className="zoom-controls">
                    <button onClick={() => setZoom(z => Math.min(z + 0.1, 2))}>
                        <img src={zoomIn} alt="Zoom In" />
                    </button>
                    <button onClick={() => setZoom(z => Math.max(z - 0.1, 0.5))}>
                        <img src={zoomOut} alt="Zoom Out" />
                    </button>
                    <button onClick={() => setZoom(1)}>🔄 Reset</button>
                </div> */}

                <div className="folderMove-structure-wrapper">
                    <div className="folderMove-structure">
                        <div
                            className="folderMove-content-inner"
                            style={{ transform: `scale(${zoom})`, transformOrigin: "top left" }}
                        >
                            {treeData ? (
                                treeData.map(node => renderNode(node))
                            ) : (
                                <div className="loadingMove-container">
                                    <img src={loadingGif} alt="Chargement..." className="loading-gif" />
                                    <p>Chargement...</p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {selectedPath && (
                    <div className="selectedMove-path">
                        <strong>Destination :</strong> {selectedPath}/{object.name}
                    </div>
                )}

                <div className="flex justify-end -popupMove-buttons">
                    <button className="btn btn-success mr-2" onClick={handleConfirm} disabled={!selectedPath}>
                        Confirmer
                    </button>
                    <button  className="btn" onClick={onClose}>Annuler</button>
                </div>
            </div>
        </div>
    );
};

export default PopUpMoveObject;
